import Cookies from 'js-cookie';
import * as jwt from 'jsonwebtoken';
import { InvalidTokenError, jwtDecode } from 'jwt-decode';

export const ACCESS_TOKEN_KEY: string = import.meta.env.VITE_ACCESS_TOKEN_KEY || 'access_token';
export const REFRESH_TOKEN_KEY: string = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refresh_token';

const MOCKED_TOKEN = 'mocked-token';

type LoginServerPayloadT = jwt.JwtPayload & {
  authorized?: boolean;
  client_id?: string;
  tenant_id?: string;
  user_id?: string;
};

// Cookies env var helpers
export const cookiesDomain = () => import.meta.env.VITE_COOKIES_DOMAIN || '.cashnetusa.com';
export const cookiesSecure = () => import.meta.env.VITE_COOKIES_SECURE === 'true';
// This is needed when setting or deleting cookies. It's not needed when getting cookies.
export const defaultCookieAttrs = (): Cookies.CookieAttributes => ({
  domain: cookiesDomain(),
  path: '/',
  secure: cookiesSecure(),
});
export const defaultSessionAttrs = (): Cookies.CookieAttributes => ({
  domain: cookiesDomain(),
  httpOnly: true,
  path: '/',
  secure: false,
});

export type CookieJarT = {
  expire: () => void;
  getAccessToken: () => string;
  getAccountId: () => [string | undefined, Error | undefined];
  getRefreshToken: () => string;
  getTenant: () => [string | undefined, Error | undefined];
  hasSession: () => boolean;
  isTokenExpired: () => boolean;
  saveTokens: (accessToken: string, refreshToken: string) => void;
};

export const decodeToken = (token: string): [LoginServerPayloadT | undefined, Error | undefined] => {
  try {
    const decodedToken = jwtDecode<jwt.JwtPayload>(token);

    return [decodedToken as LoginServerPayloadT, undefined];
  } catch (err) {
    return [undefined, new Error(`Error decoding token: ${(err as InvalidTokenError)?.message}`)];
  }
};

export const cookieJar: CookieJarT = {
  expire: () => {
    Cookies.remove(ACCESS_TOKEN_KEY, defaultCookieAttrs());
    Cookies.remove(REFRESH_TOKEN_KEY, defaultCookieAttrs());
  },
  getAccessToken: () => Cookies.get(ACCESS_TOKEN_KEY)
    ?? (import.meta.env.VITE_MOCKING === 'true' ? MOCKED_TOKEN : ''),
  getAccountId: () => {
    if (import.meta.env.VITE_MOCKING === 'true') return ['123456', undefined];

    const accessToken = cookieJar.getAccessToken();

    if (!accessToken) return [undefined, new Error('No access token found')];

    const [decodedToken, error] = decodeToken(accessToken);

    return [decodedToken?.user_id, error];
  },
  getRefreshToken: () => Cookies.get(REFRESH_TOKEN_KEY)
    ?? (import.meta.env.VITE_MOCKING === 'true' ? MOCKED_TOKEN : ''),
  getTenant: () => {
    if (import.meta.env.VITE_MOCKING === 'true') return ['cashnetusa', undefined];
    const accessToken = cookieJar.getAccessToken();

    if (!accessToken) return [undefined, new Error('No access token found')];

    const [decodedToken, error] = decodeToken(accessToken);

    return [decodedToken?.tenant_id, error];
  },
  hasSession: () => Boolean(cookieJar.getAccessToken() || cookieJar.getRefreshToken()),
  isTokenExpired: () => {
    const [decodedToken] = decodeToken(cookieJar.getAccessToken());

    if (!decodedToken) return true;

    // exp format: https://datatracker.ietf.org/doc/html/rfc7519#section-4.1.4
    return !decodedToken?.exp || Date.now() >= decodedToken.exp * 1000;
  },
  saveTokens: (accessToken: string, refreshToken: string) => {
    Cookies.set(ACCESS_TOKEN_KEY, accessToken, defaultCookieAttrs());
    Cookies.set(REFRESH_TOKEN_KEY, refreshToken, defaultCookieAttrs());
  },
};
